import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import type { ResourceDetail } from '@bekebi/contracts/api';
import { SERVICE_TYPE_LABELS } from '@bekebi/domain';
import { ApiError } from '../../api/client';
import { isUuid } from '../../api/ids';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useServices } from '../services/api';
import { DefinitionList, Section, UnavailableNote } from '../services/components';
import { formatDateTime } from '../services/format';
import { useAllResources, useRemoveBinding, useResource } from './api';
import { usageLabel } from './infrastructure-overview';
import { BindServiceForm, EditResourceForm } from './resource-forms';

/**
 * One resource (/app/infrastructure/:resourceId): what it is, what it is part of and contains,
 * and which services are bound to it. Binding and unbinding change only the relationship — never
 * the service or the resource. The inventory holds no status or health, and no credentials.
 */
export function ResourceDetailPage() {
  const { resourceId } = useParams();
  if (!isUuid(resourceId)) return <ResourceNotFound />;
  return <ResourceDetailFor id={resourceId} />;
}

function ResourceNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Resource not found</h1>
      <p className="mt-2 text-sm text-muted">
        There is no infrastructure resource at this address.
      </p>
      <p className="mt-4 text-sm">
        <Link
          to="/app/infrastructure"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Back to Infrastructure
        </Link>
      </p>
    </div>
  );
}

function ResourceDetailFor({ id }: { id: string }) {
  const resource = useResource(id);
  if (resource.isPending) return <LoadingState label="Loading resource…" />;
  if (resource.isError) {
    if (resource.error instanceof ApiError && resource.error.status === 404) {
      return <ResourceNotFound />;
    }
    return (
      <ErrorState
        title="The resource could not be loaded"
        message={resource.error.message}
        onRetry={() => void resource.refetch()}
      />
    );
  }
  return <ResourceView resource={resource.data} />;
}

function ResourceView({ resource }: { resource: ResourceDetail }) {
  const canManage = useHasPermission('infrastructure.manage');
  const allResources = useAllResources(resource.parentResourceId !== null);
  const parent = allResources.data?.find((r) => r.id === resource.parentResourceId);
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-sm">
          <Link
            to="/app/infrastructure"
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            Infrastructure
          </Link>
        </p>
        <h1 className="mt-2 break-words">{resource.name}</h1>
        <p className="mt-1 text-sm text-muted">
          Kind: <span className="font-mono">{resource.kind}</span>
        </p>
        {notice ? (
          <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
            {notice}
          </p>
        ) : null}
      </div>

      <Section title="Resource">
        <DefinitionList
          items={[
            [
              'Kind',
              <span key="k" className="font-mono">
                {resource.kind}
              </span>,
            ],
            [
              'External reference',
              resource.externalRef ? (
                <span key="e" className="break-all font-mono">
                  {resource.externalRef}
                </span>
              ) : (
                'None recorded'
              ),
            ],
            [
              'Part of',
              resource.parentResourceId ? (
                <Link
                  key="p"
                  to={`/app/infrastructure/${resource.parentResourceId}`}
                  className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                >
                  {parent ? parent.name : 'Parent resource'}
                </Link>
              ) : (
                'Not part of another resource'
              ),
            ],
            ['Used by', usageLabel(resource.usage.linkedServiceCount)],
            ['Status', 'Status not available — the inventory does not record one.'],
            ['Recorded', formatDateTime(resource.createdAt)],
            ['Last changed', formatDateTime(resource.updatedAt)],
          ]}
        />
        {resource.children.length > 0 ? (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-fg">Contains</h3>
            <ul aria-label="Contained resources" className="mt-1 space-y-1 text-sm">
              {resource.children.map((child) => (
                <li key={child.id}>
                  <Link
                    to={`/app/infrastructure/${child.id}`}
                    className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                  >
                    {child.name}
                  </Link>{' '}
                  <span className="font-mono text-xs text-muted">{child.kind}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>

      <BoundServicesSection resource={resource} canManage={canManage} />

      {canManage ? (
        <>
          <Section
            title="Bind a service"
            description="Record that a service runs on or uses this resource."
          >
            <BindServiceForm resource={resource} />
          </Section>
          <Section title="Edit" description="The kind cannot be changed.">
            <EditResourceForm resource={resource} />
          </Section>
        </>
      ) : null}
    </div>
  );
}

function BoundServicesSection({
  resource,
  canManage,
}: {
  resource: ResourceDetail;
  canManage: boolean;
}) {
  const services = useServices();
  const [removed, setRemoved] = useState<string | null>(null);
  const byId = new Map((services.data ?? []).map((s) => [s.id, s]));

  return (
    <Section
      title="Bound services"
      description="Service health is shown on each service; it is not a property of the resource."
    >
      {removed ? (
        <p role="status" className="mb-3 text-sm text-success">
          {removed}
        </p>
      ) : null}
      {resource.linkedServices.length === 0 ? (
        <UnavailableNote>No services are currently bound to this resource.</UnavailableNote>
      ) : (
        <ul aria-label="Bound services" className="divide-y divide-line">
          {resource.linkedServices.map((link) => {
            const service = byId.get(link.serviceId);
            const label = service?.name ?? SERVICE_TYPE_LABELS[link.serviceType];
            return (
              <li key={link.bindingId} className="py-3 first:pt-0 last:pb-0">
                <BindingRow
                  label={label}
                  serviceId={link.serviceId}
                  bindingId={link.bindingId}
                  role={link.role}
                  scopeRef={link.scopeRef}
                  canManage={canManage}
                  onRemoved={() => setRemoved(`The binding to ${label} was removed.`)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/** A binding, with an inline confirmation before it is removed (only the link is removed). */
function BindingRow({
  label,
  serviceId,
  bindingId,
  role,
  scopeRef,
  canManage,
  onRemoved,
}: {
  label: string;
  serviceId: string;
  bindingId: string;
  role: string;
  scopeRef: string | null;
  canManage: boolean;
  onRemoved: () => void;
}) {
  const remove = useRemoveBinding();
  const [confirming, setConfirming] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);

  // Focus moves into the confirmation when it opens, and back to its button when cancelled.
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
    else if (wasConfirming.current) openRef.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to={`/app/services/${serviceId}`}
            className="font-semibold text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            {label}
          </Link>
          <p className="text-muted">
            Role: <span className="font-mono">{role}</span>
            {scopeRef ? (
              <>
                {' · '}Scope: <span className="break-all font-mono">{scopeRef}</span>
              </>
            ) : null}
          </p>
        </div>
        {canManage && !confirming ? (
          <Button
            ref={openRef}
            variant="secondary"
            aria-label={`Remove binding to ${label}`}
            onClick={() => setConfirming(true)}
          >
            Remove binding
          </Button>
        ) : null}
      </div>
      {confirming ? (
        <div
          role="group"
          aria-label={`Confirm removing the binding to ${label}`}
          className="rounded-md border border-warning-line bg-warning-soft p-3"
        >
          <p className="text-warning">
            Remove this binding? Only the link between {label} and this resource is removed; the
            service and the resource themselves are not deleted.
          </p>
          {remove.isError ? (
            <p role="alert" className="mt-2 text-danger">
              {remove.error.message}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(
                  { serviceId, bindingId },
                  {
                    onSuccess: () => {
                      setConfirming(false);
                      onRemoved();
                    },
                  },
                )
              }
            >
              {remove.isPending ? 'Removing…' : 'Remove binding'}
            </Button>
            <Button
              ref={cancelRef}
              variant="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
